import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import { getValidLocale } from "@backpacker/intl";
import { Radius, Spacing, content, transparentize } from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import { useSize } from "@lkf/hooks";
import Tooltip from "@ui-kit/tooltip";
import { tr } from "@radarly/i18n";
import { Cluster } from "@radarly/models/insights";
import {
  GRID_ROW_GAP,
  PICTURE_GRID_GAP,
  PICTURE_GRID_WIDTH,
  POSITION_INDICATOR_WIDTH,
} from "./dimensions";
import Image from "./image";

export interface IProps {
  clusters: Cluster[];
  locale: Locale;
  isFullscreen?: boolean;
  gridImageFullscreenSize: number;
  onOpenCluster?: (cluster: Cluster) => void;
}

const Row = styled.div<{
  isFullscreen: boolean;
  gridImageFullscreenSize: number;
}>`
  display: flex;
  align-items: center;
  border-radius: ${Radius.MediumLarge};
  overflow: hidden;
  cursor: pointer;
  padding: ${Spacing.ExtraSmall};
  height: ${({ isFullscreen, gridImageFullscreenSize }) =>
    isFullscreen
      ? `${gridImageFullscreenSize + GRID_ROW_GAP}px`
      : `${PICTURE_GRID_WIDTH + GRID_ROW_GAP}px`};
  overflow: hidden;
  mask-image: linear-gradient(
    to left,
    rgba(0, 0, 0, 0) 0,
    rgba(0, 0, 0, 1) 150px
  );
  &:hover {
    background-color: ${(props) =>
      transparentize(0.8, content(props, "secondary"))};
  }
`;

const Position = styled.div`
  color: ${(props) => content(props, "secondary")};
  margin-right: ${Spacing.Medium};
`;

const Pictures = styled.div`
  display: flex;
  gap: ${Spacing.Medium};
  border-radius: ${Radius.MediumLarge};
  overflow: hidden;
  grid-gap: 3px;
  overflow: hidden;
  flex-shrink: 0;
`;

const ImageClustersGrid = ({
  clusters,
  locale,
  onOpenCluster,
  isFullscreen,
  gridImageFullscreenSize,
}: IProps) => {
  const canonicalLocale = getValidLocale(locale);
  //@ts-ignore ListFormat is correct but not typed by typescript yet.
  const intl = new Intl.ListFormat(canonicalLocale);
  const ref = useRef(null);
  const { width } = useSize(ref);
  const [visibleClusters, setVisibleClusters] = useState(clusters);

  const getVisibleClusters = useCallback(() => {
    if (isFullscreen) {
      return setVisibleClusters(clusters);
    }
    // maxPictures keeps the number of pictures that can fit in a row given the card's width.
    // Then we add another image to the total so as to make sure we leave no empty space in the row (maybe there's 30px of empty space left)
    const pictureWidth = isFullscreen
      ? gridImageFullscreenSize
      : PICTURE_GRID_WIDTH;
    const maxPictures =
      Math.floor(
        (width - POSITION_INDICATOR_WIDTH) / (pictureWidth + PICTURE_GRID_GAP)
      ) + 1;
    const filteredClusters = [...clusters].map((cluster) => ({
      ...cluster,
      hits: [...cluster.hits].splice(0, maxPictures),
    }));
    setVisibleClusters(filteredClusters);
  }, [clusters, gridImageFullscreenSize, isFullscreen, width]);

  useLayoutEffect(() => {
    window.addEventListener("resize", getVisibleClusters);
    getVisibleClusters();
    return () => {
      window.removeEventListener("resize", getVisibleClusters);
    };
  }, [getVisibleClusters]);

  return (
    <div>
      {visibleClusters.map((cluster, i) => (
        <Tooltip
          key={i}
          tooltip={
            <div>
              <p>
                {tr(locale, "TOOLTIPS::ANALYTICS::DOC_PLURAL", {
                  label: clusters[i].total,
                })}
              </p>
              <p>{intl.format(clusters[i].keywords.slice(0, 4))}</p>
            </div>
          }
        >
          <Row
            key={i}
            ref={ref}
            isFullscreen={isFullscreen}
            gridImageFullscreenSize={gridImageFullscreenSize}
            onClick={() => onOpenCluster(clusters[i])}
          >
            <Position>#{i + 1}</Position>
            <Pictures>
              {cluster.hits.map((post) => (
                <Image
                  key={post.uid}
                  post={post}
                  display="row"
                  isFullscreen={isFullscreen}
                  gridImageFullscreenSize={gridImageFullscreenSize}
                />
              ))}
            </Pictures>
          </Row>
        </Tooltip>
      ))}
    </div>
  );
};

export default ImageClustersGrid;
